import { useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'

import { InventarioDeSobrantes } from './InventarioDeSobrantes'
import { MovimientosDeSobrantes } from './MovimientosDeSobrantes'
import { PorVerificar } from './PorVerificar'
import {
  useInventarioDeSobrantes,
  useSobrantesPorVerificar,
} from './useSobrantes'
import estilos from './PaginaSobrantes.module.css'

/**
 * INVENTARIO DE SOBRANTES (0310).
 *
 * Johan: «todo ese material que se va verificando que sobró del trabajo entra
 * a una sección nueva que se llama inventario de sobrantes, donde se van
 * acumulando todos los materiales sobrantes que van quedando de los trabajos».
 *
 * Tres vistas de lo mismo, en el orden en que se trabaja: lo que los técnicos
 * reportaron y falta contar, lo que hay, y por dónde entró y salió cada cosa.
 */
type Vista = 'verificar' | 'inventario' | 'movimientos'

export function PaginaSobrantes() {
  const porVerificar = useSobrantesPorVerificar()
  const inventario = useInventarioDeSobrantes()
  const [vista, setVista] = useState<Vista | null>(null)

  if (porVerificar.isLoading || inventario.isLoading) {
    return <Cargando mensaje="VERIFICANDO SOBRANTES DISPONIBLES..." />
  }
  const fallo = porVerificar.error ?? inventario.error
  if (fallo) return <Aviso tipo="error">{(fallo as Error).message}</Aviso>

  const pendientes = porVerificar.data ?? []
  const saldos = inventario.data ?? []
  const conSaldo = saldos.filter((s) => s.saldo > 0).length
  const ordenes = new Set(pendientes.map((p) => p.orden_trabajo_id)).size

  // Abre donde hay trabajo: si hay algo por contar, ahí; si no, el inventario.
  const vistaViva: Vista = vista ?? (pendientes.length > 0 ? 'verificar' : 'inventario')

  const VISTAS: [Vista, string, number | null][] = [
    ['verificar', 'Por verificar', pendientes.length],
    ['inventario', 'Inventario', conSaldo],
    ['movimientos', 'Movimientos', null],
  ]

  return (
    <div className={estilos.pagina}>
      <header className={estilos.cabecera}>
        <div className={estilos.vistas} role="tablist" aria-label="Qué ver">
          {VISTAS.map(([valor, texto, cuenta]) => (
            <button
              key={valor}
              type="button"
              role="tab"
              aria-selected={vistaViva === valor}
              className={vistaViva === valor ? estilos.vistaViva : undefined}
              onClick={() => setVista(valor)}
            >
              {texto}
              {cuenta !== null && <span className={estilos.cuenta}>{cuenta}</span>}
            </button>
          ))}
        </div>
        <p className={estilos.resumen}>
          {pendientes.length > 0
            ? `${pendientes.length} material${pendientes.length === 1 ? '' : 'es'} de ${ordenes} ${
                ordenes === 1 ? 'orden' : 'órdenes'
              } por contar.`
            : 'Nada pendiente por contar.'}
        </p>
      </header>

      {vistaViva === 'verificar' && <PorVerificar pendientes={pendientes} />}
      {vistaViva === 'inventario' && <InventarioDeSobrantes saldos={saldos} />}
      {vistaViva === 'movimientos' && <MovimientosDeSobrantes />}
    </div>
  )
}
